import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatValue, pctColor } from "@/lib/api";
import { enablePush, pushEnabled, pushSupported } from "@/lib/push";
import { Bell, TrendingUp, TrendingDown, BellRing } from "lucide-react";

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(localStorage.getItem("de_alerts_seen") || "");
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/notifications").then(r => {
      setAlerts(r.data.alerts || []);
      setGeneratedAt(r.data.generated_at);
    }).catch(() => {});
    pushEnabled().then(setPushOn).catch(() => {});
  }, []);

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await enablePush();
      setPushOn(true);
    } catch (e) {
      alert(e.message || "Could not enable alerts");
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = generatedAt && generatedAt !== seen ? alerts.length : 0;

  const toggle = () => {
    setOpen(o => !o);
    if (!open && generatedAt) { localStorage.setItem("de_alerts_seen", generatedAt); setSeen(generatedAt); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative w-9 h-9 rounded-md border border-[var(--border)] bg-[var(--surface)] flex items-center justify-center text-[var(--text-2)] hover:text-white transition-colors" data-testid="notification-bell">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[var(--accent)] text-black text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 card-bordered bg-[var(--surface)] z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
            <span className="text-xs tracking-widest text-[var(--text-3)]">MARKET MOVERS</span>
            {pushSupported() && (
              pushOn ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-[var(--accent)]" data-testid="push-enabled"><BellRing className="w-3 h-3" /> Alerts on</span>
              ) : (
                <button onClick={handleEnablePush} disabled={pushBusy} className="text-[10px] text-[var(--accent)] hover:underline" data-testid="enable-push">
                  {pushBusy ? "Enabling…" : "Enable browser alerts"}
                </button>
              )
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 && <div className="p-4 text-sm text-[var(--text-3)]">No big moves right now.</div>}
            {alerts.map((a, i) => (
              <Link to={`/app/players/${a.player_id}`} key={i} onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)] last:border-0 hover:bg-white/[0.03]" data-testid={`notification-${i}`}>
                {a.kind === "riser" ? <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0" /> : <TrendingDown className="w-4 h-4 text-red-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{a.name} <span className="text-[var(--text-3)] text-xs">· {a.position}</span></div>
                  <div className="text-[10px] text-[var(--text-3)]">Value {formatValue(a.value)}</div>
                </div>
                <span className={`text-sm tabular ${pctColor(a.change_pct)}`}>{a.change_pct > 0 ? "+" : ""}{a.change_pct}%</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
